import type { Message } from "@dndbeyond/message-broker-lib";
import type { Blending, Side, Vector3Tuple, Vector4Tuple } from "three";
import type {
  DiceVisibilities,
  DieEventTypes,
  DieTypes,
  RollKinds,
  RollTos,
  RollTypes,
  RollWiths,
} from "./constants";
import type { DiceOperations, DieEventValues } from "./enums";

export type DiceSet = {
  id: string;
  setId: string;
  name: string;
  fullName: string;
  avatarUrl: string;
  manifestUrl: string;
};

/**
 * Maps filenames to blob URLs for custom uploaded dice sets.
 * Example: { "manifest.json": "blob:http://...", "d20.glb": "blob:http://..." }
 */
export type CustomUrlMap = Record<string, string>;
type Entitlement = {
  accessTypes: {
    [key: string]: number;
  };
  definitionData: DiceSet[];
};
export type DiceFamily = {
  id: string;
  name: string;
  description: string;
  rarity: number;
  sets: Entitlement;
};

export type AllFamilySets = {
  data: DiceFamily[];
};

export type ObjectValues<T> = T[keyof T];

export type FrameData = {
  position: [number, number, number];
  rotation?: [number, number, number, number];
  collisionVolume?: number;
};

export type DieType = ObjectValues<typeof DieTypes>;

export type DieEventType = ObjectValues<typeof DieEventTypes>;
export type CurrentEvent = {
  trigger: string;
};

export type Die = {
  id: string;
  rollId: string;
  setId: string;
  groupId?: string;
  dieType: DieType;
  linearVelocity?: [number, number, number];
  position?: [number, number, number];
  rotation?: [number, number, number];
  frameData: FrameData[];
  settledValue?: number;
  closestNormal?: [number, number, number];
  offset?: [number, number, number];
  upsideValue?: number;
  lastStandardEvent?: DieEventValues;
};

export type Entity = { id: string | number };

export type Entities<T extends Entity> = {
  ids: T["id"][];
  entities: Record<T["id"], T>;
};

export type RollTo = ObjectValues<typeof RollTos>;

export type DiceRollEventPayload = {
  rollId: string;
  diceRolled: {
    d4?: number;
    d6?: number;
    d8?: number;
    d10?: number;
    d12?: number;
    d20?: number;
    d100?: number;
  };
  results: {
    d4?: number[];
    d6?: number[];
    d8?: number[];
    d10?: number[];
    d12?: number[];
    d20?: number[];
    d100?: number[];
  };
  total: number;
  action: string;
  type: string;
  kind?: string;
  diceSet: string;
  audience: RollTo;
  isDm?: boolean;
  eventType: string;
};

export type RollDiceParams = {
  diceNotation: string;
  action: string;
  rollType?: RollType;
  rollKind?: RollKind;
  entityId: string;
  entityType: string;
  userId?: string;
  setId: string;
  gameId?: string;
  name?: string;
  avatarUrl?: string;
  valueOverride?: number;
  operand?: number;
  dmId?: string;
};

export type DieTypeQuantities = {
  d4: number;
  d6: number;
  d8: number;
  d10: number;
  d12: number;
  d20: number;
  d100: number;
};

export type AnimationGroup = {
  name: string;
  from: number;
  to: number;
  targetedAnimations: TargetedAnimation[];
  metadata?: {
    uvOffsets?: [number, number][];
  };
};

export type TargetedAnimation = {
  animation: Animation;
  // Every single animation targetId follows this naming pattern:
  // "Clone of [meshName].[meshName]"
  targetId: string;
};

export type Animation = {
  name: string;
  // Property path relative to the target node.
  // Examples: "scaling", "visibility", "material.emissiveColor", "material.glitterScale"
  property: string;
  framePerSecond: number;
  dataType: AnimationDataType;
  blendingSpeed?: number;
  keys: AnimationKeyframe[];
};

export type AnimationKeyframe = {
  frame: number;
  values: Array<number | CustomAnimationValue>;
};

// Only custom animation value we seem to support currently, but more could go here
export type CustomAnimationValue = "randomRotation";

// Mirrors Babylon Animation.ANIMATIONTYPE_* constants
export const DataTypes = {
  NUMBER: 0,
  VECTOR: 1,
  COLOR: 4,
} as const;

export type AnimationDataType = ObjectValues<typeof DataTypes>;

export type RollResult = {
  constant: number;
  text: string;
  total: number;
  values: number[];
};

export type DiceNotationSet = {
  count: number;
  dice: {
    dieType: DieType;
    dieValue?: number;
  }[];
  dieType: DieType;
  operation: DiceOperations;
  operand?: number;
};

export type Set = (DiceNotationSet | undefined)[];

export type DiceNotation = {
  constant: number;
  set: Set;
};
export type RollDicePayload = {
  data: {
    action: string;
    context: RollContext;
    rollId?: string;
    rolls: {
      diceNotation: DiceNotation;
      rollKind?: RollKind;
      rollType: RollType;
      result?: RollResult;
    }[];
    setId?: string;
  };
  dateTime: string;
  entityId: string;
  entityType: string;
  eventType: string;
  userId?: string;
  gameId?: string;
  id: string;
  messageScope: string;
  messageTarget: string;
  persist: boolean;
  source: string;
};

export type DieProperties = {
  numberTexture?: string;
  dissolveTexture?: string;
  glitterPatternTexture?: string;
  glitterNoiseTexture?: string;
  glitterScale?: number;
  useNumbers?: boolean;
  numberColor?: string;
  numberHighlightColor?: string;
  numberBumpScale?: number;
  numbersMode?: number;
  marbleAmplitude?: number;
  marbleTiming?: number;
  marbleZoom?: number;
};

export type BasicMaterial = DieProperties & {
  albedoTexture?: string;
  albedoTextureClone?: boolean;
  bumpTexture?: string;
  metallicTexture?: string;
  roughnessTexture?: string;
  albedoColor?: string;
  roughness?: number;
  metallic?: number;
  alpha?: number;
  emissiveTexture?: string;
  emissiveTextureClone?: boolean;
  emissiveColor?: string;
  emissiveIntensity?: number;
  useRoughnessFromMetallicTextureGreen?: boolean;
  useAmbientOcclusionFromMetallicTextureRed?: boolean;
  clearCoatIOR?: number;
  clearCoatRoughness?: number;
  clearCoatTexture?: string;
  clearCoatBumpTexture?: string;
  ambientTexture?: string;
  ambientTextureStrength?: number;
  clearCoat?: {
    intensity?: number;
    roughness?: number;
    bumpTexture?: string;
  };
  clearCoatTintColor?: string;
  indexOfRefraction?: number;
  refractionTexture?: string;
  environmentIntensity?: number;
  backFaceCulling?: boolean;
  alphaMode?: number;
  transparencyMode?: number;
  opacityTexture?: string;
  opacityTextureClone?: boolean;
  metallicReflectanceColor?: string;
  tintColor?: string;
  subSurface?: {
    tintColor?: string;
    refractionIntensity?: number;
    refractionTexture?: string;
    volumeIndexOfRefraction?: number;
    thicknessTexture?: string;
    minimumThickness?: number;
    maximumThickness?: number;
  };
  normalScale?: [number, number];
  // a boolean we can set to true to tell the material system
  // to skip the attempt to map babylon transparency properties to threejs
  useThreeTransparency?: boolean;
  transparent?: boolean;
  opacity?: number;
  depthWrite?: boolean;
  depthTest?: boolean;
  blending?: Blending;
  alphaTest?: number;
  side?: Side;
  specularColorMap?: string;
};

export type Shader = {
  vertexShader?: string;
  fragmentShader: string;
};

export type Uniforms = Record<
  string,
  { type: "color" | "texture" | "float" | "int"; value: string | number }
>;

// The meta data for a particle system, defined in the manifest
type ManifestParticleSystem = {
  particleJson: string;
  particleTexture: string;
  uniforms?: Uniforms;
};

export type ParticleSystemGradient = { gradient: number; factor1: number }[];
export type ParticleSystemColorGradient = {
  gradient: number;
  color1: Vector4Tuple;
  color2?: Vector4Tuple;
}[];

// This is the particle system definition from Babylon, which is how all of the
// existing sets are defined. We can refactor this in the future to be more
// 3js-forward.
export type ParticleSystem = {
  name: string;
  id: string;
  capacity: number;
  emitter: Vector3Tuple;
  particleEmitterType: {
    type: string;
    radius: number;
    radiusRange: number;
    directionRandomizer: number;
  };
  invertY: boolean;
  // animations: any[]; // not currently used
  useRampGradients: boolean;
  beginAnimationOnStart: boolean;
  beginAnimationFrom: number;
  beginAnimationTo: number;
  beginAnimationLoop: boolean;
  startDelay: number;
  renderingGroupId: number;
  isBillboardBased: boolean;
  billboardMode: number;
  minAngularSpeed: number;
  maxAngularSpeed: number;
  minSize: number;
  maxSize: number;
  minScaleX: number;
  maxScaleX: number;
  minScaleY: number;
  maxScaleY: number;
  minEmitPower: number;
  maxEmitPower: number;
  minLifeTime: number;
  maxLifeTime: number;
  targetStopDuration: number;
  emitRate: number;
  emitRateGradients: ParticleSystemGradient;
  sizeGradients: ParticleSystemGradient;
  gravity: Vector3Tuple;
  octaves: number;
  noiseStrength: Vector3Tuple;
  color1: Vector4Tuple;
  color2: Vector4Tuple;
  colorDead: Vector4Tuple;
  colorGradients: ParticleSystemColorGradient;
  updateSpeed: number;
  blendMode: number;
  preWarmCycles: number;
  preWarmStepOffset: number;
  minInitialRotation: number;
  maxInitialRotation: number;
  startSpriteCellID: number;
  endSpriteCellID: number;
  spriteCellChangeSpeed: number;
  spriteCellWidth: number;
  spriteCellHeight: number;
  spriteRandomStartCell: boolean;
  spriteCellLoop: boolean;
  isAnimationSheetEnabled: boolean;
  textureMask: Vector4Tuple;
  preventAutoStart: boolean;
  isLocal: boolean;
  customShader: {
    shaderPath: {
      fragmentElement: string;
    };
    shaderOptions: {
      defines: string[];
      uniforms: string[];
      samplers: string[];
    };
  } | null;
};

export type Trigger = {
  definition: string;
  triggerStart: string;
  triggerStop?: string | string[];
  loop?: boolean;
  value?: number;
};

export type RandomTrigger = (Trigger | Trigger[])[];

export type ManifestNode = {
  modelFile: string;
  billboardMode?: number;
  scale?: number;
  animations: (Trigger | RandomTrigger)[];
  particleSystems: (Trigger | RandomTrigger)[];
  sounds: (Trigger | RandomTrigger)[];
  // a material value of string points to a shared material defined in resources
  materials: Record<string, MaterialResponse | string>;
  siblingNodes?: string[];
  stackSiblings?: number;
  randomRotation?: boolean;
};

export type ManifestNodes = {
  [key: string]: ManifestNode;
};

export type ShaderTexture = {
  shader: string;
  uniforms?: Uniforms;
};

export type AnimationGroupClones = {
  animationJson: string;
  animationClones: Record<
    string,
    {
      name: string;
      targetedAnimations: Pick<TargetedAnimation, "targetId">[];
    }
  >;
};

type Resources = {
  models: Record<string, string>;
  shaders: Record<string, Shader>;
  textures: Record<string, string | ShaderTexture>;
  particleSystems: Record<string, ManifestParticleSystem>;
  sounds: Record<string, string>;
  animationGroups: Record<string, string | AnimationGroupClones>;
  materials: Record<string, MaterialResponse>;
};

export type Manifest = {
  // the dice set's slug/codename, e.g., "clash-of-thorns"
  id: string;
  slug: string;
  overrideSetId?: string;
  resources: Resources;
  animationGroups?: Record<string, AnimationGroup>;
  shaders?: Record<string, Shader>;
  particleSystems?: Record<string, ParticleSystem>;
} & ManifestNodes;

export type MaterialResponse = {
  properties?: BasicMaterial;
  disableShadow?: boolean;
  nodeJson?: string;
  snippets?: string[];
  shader?: string;
  uniforms?: Uniforms;
};

export type NodeResponse = {
  [key: string]: ManifestNode;
};

export type ManifestResponse = {
  $schema: string;
  setName: string;
  resources: Resources;
} & NodeResponse;

export type DiceVisibility = ObjectValues<typeof DiceVisibilities>;

export type DiceData = {
  familyId: string;
  id: string;
  manifestUrl: string;
  setId: string;
  settings: UserSettings;
};

// DeepPartial - recursively makes all properties optional
export type DeepPartial<T> =
  T extends Array<infer U>
    ? Array<DeepPartial<U>>
    : T extends object
      ? { [K in keyof T]?: DeepPartial<T[K]> }
      : T;

export type StatsData = {
  fps: number;
  ms: number;
  calls: number;
  triangles: number;
  points: number;
  lines: number;
  geometries: number;
  textures: number;
  programs: number;
};

export type MessageWithTimer = Message & { diceHandlingTimer?: number };

export type Roll = {
  id: string;
  message: MessageWithTimer;
  isFinished?: boolean;
};

export type RollWith = ObjectValues<typeof RollWiths>;

export type UserSettings = {
  shadowQuality: number;
  particlesEnabled: boolean;
  volume: number;
  isPostProcessingEnabled: boolean;
  visibility: DiceVisibility;
};

export type RollType = ObjectValues<typeof RollTypes>;

export type RollContext = {
  entityId?: string;
  entityType?: string;
  messageScope?: string;
  messageTarget?: string | number;
  name?: string;
  avatarUrl?: string;
  userId?: string;
};

export type RollKind = ObjectValues<typeof RollKinds> | "";

export type RollRequestRoll = {
  diceNotation: DiceNotation;
  rollType: RollType;
  rollKind?: RollKind;
  result?: RollResult;
};
